import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2Icon } from 'lucide-react';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { SubpageHero } from '../../components/sections/subpages/SubpageHero';
import { PatternDecor } from '../../components/ui/PatternDecor';
export function PartnershipScholarships() {
  return <>
      <SubpageHero eyebrow="Ensuring equal access and educational continuity" title="Scholarships Program" paragraphs={['The Scholarships Program ensures that financial circumstances do not prevent children from accessing structured, culturally sensitive education.', 'Allocation is transparent, criteria are clear, and reporting is regular — so families, donors, and partners all know what their support delivers.']} primaryCta={{
      label: 'Request Scholarship Details',
      to: '/en/contact'
    }} secondaryCta={{
      label: 'Support Scholarships',
      to: '/en/partnership/donate-for-children'
    }} image="https://cdn.magicpatterns.com/uploads/5n1iBHyLaErd9TVUJZictm/Scholarships-5.jpg" imageAlt="Muslim families and educators gathered with children around IILH learning materials in a bright Paris classroom" compact masked data-id="element-4842" data-mp="25je8k2h94fu9" />

      {/* What scholarships support */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-sage-50/60" data-id="element-4843" data-mp="3kx90cokmmhpl">
        <PatternDecor side="right" y="top" size="lg" data-id="element-4844" data-mp="3kz26m2rbuzx2" />
        <Container data-id="element-4845" data-mp="3pxn9k9xyrxp7">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-4846" data-mp="3px3ponor6z8j">
            What scholarships support
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-4847" data-mp="1viks8zj2jlxd">
            Scholarships support:
          </p>
          <ul className="mt-6 grid sm:grid-cols-2 gap-x-10 gap-y-3" data-id="element-4848" data-mp="1zjczjn8drgr0">
            {["Access to structured Children's Programs", 'Online Learning subscriptions and digital tools', 'Mentorship and tutoring sessions where available', 'Learning materials and family resources'].map(item => <li key={item} className="flex items-start gap-3 text-ink-700" data-id="element-4849" data-mp="3bt5i71sz4yrn">
                <CheckCircle2Icon size={16} className="text-forest-700 mt-1 flex-shrink-0" strokeWidth={1.8} data-id="element-4850" data-mp="37sll1y8ehv5i" />
                {item}
              </li>)}
          </ul>
        </Container>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-24 bg-white/60" data-id="element-4851" data-mp="3vlk7jsoyf7nt">
        <Container data-id="element-4852" data-mp="3qlfr22o1qcxl">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-4853" data-mp="3qkwl7kbo7ezx">
            How the program works
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-5" data-id="element-4854" data-mp="3foszqqaja0b">
            {[{
            title: 'Transparent allocation',
            body: 'Clear criteria for eligibility, partnership-driven funding, and structured selection.'
          }, {
            title: 'Educational continuity',
            body: 'Multi-term support so children stay enrolled and progress without interruption.'
          }, {
            title: 'Regular reporting',
            body: 'Donors and partners receive updates on outcomes, allocation, and impact.'
          }].map((card, i) => <motion.div key={card.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.06
          }} className={`rounded-2xl border border-ink-900/5 p-6 shadow-soft ${['bg-sage-100', 'bg-[#F7EDE9]', 'bg-sand-100'][i]}`} data-id="element-4855" data-mp="93a9eoldl7s7" data-mpi={i}>
                <h3 className="font-display text-lg text-ink-900" data-id="element-4856" data-mp="34pusxz4iz09m">
                  {card.title}
                </h3>
                <p className="mt-3 text-ink-700 text-sm leading-relaxed" data-id="element-4857" data-mp="30patgowhbkkb">
                  {card.body}
                </p>
              </motion.div>)}
          </div>
          <div className="mt-8 rounded-2xl bg-sage-50 border border-sage-200 px-6 py-4 text-center" data-id="element-4858" data-mp="2qql9mql0vq1f">
            <p className="text-ink-500 italic" data-id="element-4859" data-mp="2lqyw0g1a87s3">
              Please note that our scholarship fund is sustained through Sadaqah
              and corporate grants; Zakat is not accepted at this stage.
            </p>
          </div>
        </Container>
      </section>

      {/* Who can apply */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-[#EDF2F7]/60" data-id="element-4860" data-mp="fyjulhws81xr">
        <PatternDecor side="left" y="middle" size="md" data-id="element-4861" data-mp="1xvx1691gz0vo" />
        <Container data-id="element-4862" data-mp="1xvdp38yi36pk">
          <h3 className="font-display text-lg text-ink-900" data-id="element-4863" data-mp="1xtstdg0g9hq4">Who can apply</h3>
          <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-4864" data-mp="22tfa3zfhlg3o">
            Scholarships are available to Muslim families living in non-Muslim
            countries who face financial barriers to structured education. Some
            places are reserved through community partnerships. Selection is
            based on need, family commitment, and program fit.
          </p>
        </Container>
      </section>

      {/* Ready to support CTA */}
      <section className="py-16 md:py-24 bg-white/60" data-id="element-4865" data-mp="1nxzalew50rzw">
        <Container data-id="element-4866" data-mp="1nw65i416afu0">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8" data-id="element-4867" data-mp="1o02b4sux23iv">
            <div className="max-w-xl" data-id="element-4868" data-mp="1suprg4frnrmd">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-4869" data-mp="2zde8qm031pwl">
                Ready to support?
              </h2>
              <p className="mt-4 text-ink-700" data-id="element-4870" data-mp="34a4m31ki67eb">
                Choose the most relevant option — donation, scholarships, or
                institutional partnership. We'll respond quickly with next
                steps.
              </p>
            </div>
            <div className="flex flex-col items-start md:items-end gap-3" data-id="element-4871" data-mp="1e85poznileb2">
              <p className="max-w-sm text-sm text-ink-500 leading-relaxed md:text-right" data-id="element-4872" data-mp="198t22cotk37f">
                Please note: We accept Sadaqah and corporate grants; Zakat is
                not accepted at this stage.
              </p>
              <div className="flex flex-wrap gap-3" data-id="element-4873" data-mp="gkx0ouob1n4b">
                <Link to="/en/contact#general-inquiry" data-id="element-4874" data-mp="giapml27gphc">
                  <Button variant="primary" data-id="element-4875" data-mp="v9tfrbos40ef">Donate</Button>
                </Link>
                <Link to="/en/contact#general-inquiry" data-id="element-4876" data-mp="qi1laq2i5aj0">
                  <Button variant="secondary" data-id="element-4877" data-mp="qgi50zguizh8">Become a Partner</Button>
                </Link>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </>;
}